import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Table } from '@/common';
import { Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { getCeremonyTemplateColumns } from '@/helpers/CeremonyTemplateHelper';
import { useDeleteCeremonyTemplateMutation, useGetAllCeremonyTemplates } from '@/services';
import { useFilterStore } from '@/store';
import { AllowedPermissions, CeremonyTemplate, TableData } from '@/types';

export const EventTemplates = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const { organizationFilter } = useFilterStore();
  const { permissions } = useContext(AuthContext);
  const [selectedTemplate, setSelectedTemplate] = useState<CeremonyTemplate | undefined>();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);

  const allowedPermissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_EVENT_TEMPLATE,
    editPermission: PERMISSION_TYPE.UPDATE_EVENT_TEMPLATE,
    deletePermission: PERMISSION_TYPE.DELETE_EVENT_TEMPLATE,
    viewPermission: PERMISSION_TYPE.VIEW_EVENT_TEMPLATES
  };

  const { organizationPermissions } = useContext(AuthContext);
  const hasCeremonySetupAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP
  );

  const getOrganizationValue = (): number | null => {
    if (organizationFilter) {
      return organizationFilter.value as number;
    }

    return null;
  };

  const handleCreate = () => {
    navigate('/ceremony-templates/create');
  };

  const handleDelete = (ceremonyTemplateData: CeremonyTemplate) => {
    setSelectedTemplate(ceremonyTemplateData);
    setIsOpenDeleteModal(true);
  };

  const organizationId = getOrganizationValue();

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedTemplate(undefined);
  };

  const { mutate: deleteCeremonyTemplate } = useDeleteCeremonyTemplateMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const formatData = (): TableData[] => {
    return allCeremonyTemplates?.pages.flatMap((page) =>
      page.data?.items.map((ceremonyTemplate: CeremonyTemplate) => ({
        name: t(ceremonyTemplate.name),
        noOfSlots: ceremonyTemplate.slots.length,
        id: ceremonyTemplate.id,
        organization: ceremonyTemplate.organization?.name || 'N/A',
        enabled: true
      }))
    ) as TableData[];
  };

  const {
    isLoading: isLoadingCeremonyTemplates,
    data: allCeremonyTemplates,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetAllCeremonyTemplates(PAGE_SIZE, organizationId || undefined, hasCeremonySetupAccess);

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoadingCeremonyTemplates ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          {allCeremonyTemplates && (
            <Table
              data={formatData()}
              columns={getCeremonyTemplateColumns(
                permissions.includes(PERMISSION_TYPE.VIEW_ORGANIZATIONS)
              )}
              tableTitle={t('ceremonyTemplates')}
              addButtonLabel={t('addCeremonyTemplate')}
              handleCreate={handleCreate}
              handleDelete={(item) => handleDelete(item as CeremonyTemplate)}
              handleViewDetails={(item) => navigate(`/ceremony-templates/${item.id}`)}
              handleEdit={(item) => navigate(`/ceremony-templates/${item.id}/edit`)}
              permissions={allowedPermissions}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              numberOfAppliedFilters={0}
              hiddenColumnsMobile={[]}
            />
          )}
          {selectedTemplate && (
            <ConfirmationModal
              isOpen={isOpenDeleteModal}
              message={t('deleteMessage', { item: selectedTemplate.name })}
              onClose={handleCloseDeleteModal}
              onConfirm={() => deleteCeremonyTemplate(selectedTemplate?.id)}
            />
          )}
        </div>
      )}
    </div>
  );
};
